import moment from "moment";
import { cloneDeep } from "lodash";
import {
  ADD,
  ADD_REDIRECT,
  REPLACE,
  REMOVE,
  REMOVE_REDIRECT,
} from "../consts/map";
import { OUTER, SECTOR, POSTCODE } from "../consts/layers";
import { DEFAULT_TIMEBAND } from "../consts/strings";
import { getTimestampFromHHMM, isHHMMFormat } from "./date";
import { Postcode } from "./postcode";
import { timeBandsSorter } from "./routeMatrix";
import { isRedirectionRouteCode } from "./routes";

export const applyChangeToRouteConfig = ({ routeConfig, change }) => {
  const waveConfig = {
    ...(routeConfig.find(
      (config) =>
        (config.plannedReadyTimeBand === DEFAULT_TIMEBAND
          ? config.plannedReadyTimeBand
          : moment(config.plannedReadyTimeBand).format("HH:mm")) ===
        change.timeBand
    ) || {
      plannedReadyTimeBand:
        change.timeBand === DEFAULT_TIMEBAND
          ? change.timeBand
          : getTimestampFromHHMM(change.timeBand),
      outer: [],
    }),
  };

  // get affected outer
  let outerConfig = waveConfig.outer.find(
    (o) => o.outer === change.outer && o.routeType === change.planType
  );
  waveConfig.outer = waveConfig.outer.filter((o) => o !== outerConfig);
  outerConfig = outerConfig
    ? cloneDeep(outerConfig)
    : {
        outer: change.outer,
        routeType: change.planType,
      };

  outerConfig = applyRangeRemovals({
    change,
    outerConfig,
  });

  outerConfig = applyChangeBasedOnLevel({
    change,
    outerConfig,
  });

  outerConfig = applyConsolidation({
    change,
    outerConfig,
  });

  if (!outerConfig.sector?.length) delete outerConfig.sector;
  if (!outerConfig.postcode?.length) delete outerConfig.postcode;
  if (!outerConfig.routeCode) delete outerConfig.routeCode;
  if (outerConfig.sector || outerConfig.postcode || outerConfig.routeCode)
    waveConfig.outer.push(outerConfig);

  return [
    ...routeConfig.filter(
      (config) =>
        config.plannedReadyTimeBand !== waveConfig.plannedReadyTimeBand
    ),
    waveConfig,
  ].sort(timeBandsSorter);
};

const applyChangeBasedOnLevel = ({ change, outerConfig }) => {
  const changeRouteCode =
    change.consolidation.toOuter || change.consolidation.toSector
      ? null
      : change.routeCode;

  switch (change.level) {
    case OUTER:
      // if change is add, add new outer to the list
      if (change.type === ADD) {
        outerConfig.routeCode = changeRouteCode;
      } else if (change.type === REMOVE) {
        outerConfig.routeCode = null;
      } else throw new Error("Invalid map edit action");
      return outerConfig;
    // sector and postcode are different as they are nested
    case SECTOR:
      if (!outerConfig.sector) outerConfig.sector = [];
      // if change is add, add new outer to the list
      if ([ADD, ADD_REDIRECT].includes(change.type)) {
        const sectorConfig = outerConfig.sector.find(
          ({ sector }) => sector === change.sector
        );
        if (!sectorConfig)
          outerConfig.sector.push({
            sector: change.sector,
            routeCode: changeRouteCode,
          });
        else sectorConfig.routeCode = changeRouteCode;
      } else if ([REMOVE, REMOVE_REDIRECT].includes(change.type)) {
        outerConfig.sector = outerConfig.sector.filter(
          ({ sector }) => sector !== change.sector
        );
      } else throw new Error("Invalid map edit action");
      return outerConfig;
    case POSTCODE:
      if (!outerConfig.postcode) outerConfig.postcode = [];
      // if change is add/replace, add new outer to the list
      if ([ADD, REPLACE, ADD_REDIRECT].includes(change.type)) {
        const postcodeConfig = outerConfig.postcode.find(
          ({ postcode }) => postcode === change.postcode
        );
        if (!postcodeConfig)
          outerConfig.postcode.push({
            postcode: change.postcode,
            routeCode: changeRouteCode,
          });
        else postcodeConfig.routeCode = changeRouteCode;
      } else if ([REMOVE, REMOVE_REDIRECT].includes(change.type)) {
        outerConfig.postcode = outerConfig.postcode.filter(
          ({ postcode }) => postcode !== change.postcode
        );
      } else throw new Error("Invalid map edit action");
      return outerConfig;
    default:
      throw new Error(`Invalid postcode level ${change.level}`);
  }
};

const applyRangeRemovals = ({ change, outerConfig }) => {
  (change.rangeRemovals || []).forEach(({ routeCode, sectors }) => {
    outerConfig.sector = outerConfig.sector?.filter(
      (s) => !sectors.includes(s.sector) || s.routeCode !== routeCode
    );
    outerConfig.postcode = outerConfig.postcode?.filter(
      (p) =>
        !sectors.includes(Postcode.toSector(p.postcode)) ||
        p.routeCode !== routeCode
    );
  });
  return outerConfig;
};

const applyConsolidation = ({ change, outerConfig }) => {
  if (change.consolidation?.toSector)
    outerConfig = applySectorConsolidation({
      change,
      outerConfig,
    });
  if (change.consolidation?.toOuter)
    outerConfig = applyOuterConsolidation({
      change,
      outerConfig,
    });

  return outerConfig;
};

const applySectorConsolidation = ({ change, outerConfig }) => {
  outerConfig.sector = outerConfig.sector?.filter(
    (s) => s.sector !== change.sector
  );
  outerConfig.postcode = outerConfig.postcode?.filter(
    (p) => Postcode.toSector(p.postcode) !== change.sector
  );
  if (!change.consolidation.preventSectorAllocation)
    outerConfig.sector = [
      ...(outerConfig.sector || []),
      {
        sector: change.sector,
        routeCode: change.routeCode,
      },
    ];
  return outerConfig;
};

const applyOuterConsolidation = ({ change, outerConfig }) => ({
  outer: outerConfig.outer,
  routeCode: isRedirectionRouteCode(change.routeCode) ? null : change.routeCode,
  routeType: outerConfig.routeType,
  sector: [],
  postcode:
    outerConfig?.postcode?.filter((s) => s.routeCode !== change.routeCode) ||
    [],
});

export const getRedirectToRouteCode = ({
  routeConfig,
  planType,
  selectedTimeBand,
  postcode,
  minLevel = POSTCODE,
}) => {
  if (!selectedTimeBand || selectedTimeBand === DEFAULT_TIMEBAND) return null;
  else if (isHHMMFormat(selectedTimeBand))
    selectedTimeBand = getTimestampFromHHMM(selectedTimeBand);

  const waveIndex = routeConfig.findIndex(
    ({ plannedReadyTimeBand }) => plannedReadyTimeBand === selectedTimeBand
  );

  const level = Postcode.getLevel(postcode);
  const outer = Postcode.toOuter(postcode);
  const sector = Postcode.toSector(postcode);

  let routeCode;
  for (let i = waveIndex + 1; i < routeConfig.length && !routeCode; i++) {
    const outerConfig = routeConfig[i].outer.find(
      (o) => o.outer === outer && o.routeType === planType
    );
    if (!outerConfig) continue;
    if ([level, minLevel].every((l) => l === POSTCODE)) {
      const postcodeConfig = outerConfig.postcode?.find(
        (p) => p.postcode === postcode
      );
      if (postcodeConfig?.routeCode) {
        routeCode = postcodeConfig.routeCode;
        if (isRedirectionRouteCode(routeCode)) {
          routeCode = null;
          continue;
        } else break;
      }
    }
    if (![level, minLevel].some((l) => l === OUTER)) {
      const sectorConfig = outerConfig.sector?.find((s) => s.sector === sector);
      if (sectorConfig?.routeCode) {
        routeCode = sectorConfig.routeCode;
        if (isRedirectionRouteCode(routeCode)) {
          routeCode = null;
          continue;
        } else break;
      }
    }
    if (outerConfig.routeCode) routeCode = outerConfig.routeCode;
  }
  return routeCode || null;
};
